import { signProfileAsset } from "@/lib/profileAssetSigning.mjs";
// src/components/nav/MobileTabBar.jsx
import React from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import NavAlertBadge from "@/components/nav/NavAlertBadge.jsx";
import useUnreadMessages from "@/hooks/useUnreadMessages.js";
import {
  clearAvatarSessionCache,
  readAvatarSessionCache,
  writeAvatarSessionCache,
} from "@/lib/avatarSessionCache.js";
import { readLastRole, writeLastRole } from "@/lib/roleCache";
import supabase from "@/services/supabaseClient";
import { resolveMessagesTabTarget } from "@/utils/messagesNavigationState.js";

// Keep your dev keys
const DEV_DOCTOR_PHOTO_KEY = "afrimed:dev:doctor_profile_photo";
const DEV_PATIENT_PHOTO_KEY = "afrimed:dev:patient_profile_photo";
const PROFILE_AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";

/**
 * Detect any open modal / bottom sheet globally.
 * Markers we support:
 * - data-bottom-sheet="open"   (set ONLY when a sheet is actually open)
 * - role="dialog" + aria-modal="true"  (generic accessible dialogs)
 */
function computeOverlayOpen() {
  if (typeof document === "undefined") return false;
  if (document.querySelector('[data-bottom-sheet="open"]')) return true;
  if (document.querySelector('[role="dialog"][aria-modal="true"]')) return true;
  return false;
}

/**
 * Page-level “hide tab bar” marker (SSOT for CTA / booking flows)
 * Put: <div data-hide-tabbar="true"> ... </div> at the page root.
 */
function computePageHideTabs() {
  if (typeof document === "undefined") return false;
  return !!document.querySelector('[data-hide-tabbar="true"]');
}

function safeGetLocalStorage(key) {
  try {
    return window?.localStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function IconImg({ src, alt = "", className = "" }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={["h-6 w-6", className].join(" ").trim()}
      draggable="false"
    />
  );
}

function roleFromProfile(profile) {
  const raw =
    profile?.role ||
    profile?.user_role ||
    profile?.app_role ||
    profile?.type ||
    "";
  const r = String(raw).toLowerCase();

  if (r === "doctor" || r === "hcp" || r === "healthcare_provider") return "doctor";
  if (r === "patient") return "patient";
  if (r === "practice_team") return "practice_team";
  return null;
}

/**
 * 🔒 Route-forced role (doctor must win on any doctor area routes)
 * Add all “doctor world” and “patient world” routes here.
 */
function roleFromPathname(pathname) {
  const DOCTOR_PREFIXES = [
    "/home/doctor",
    "/settings/doctor",
    "/profile-settings/doctor",
    "/doctor/",
    "/doctors/dashboard",
    "/doctors/appointments",
    "/doctors/patients",
    "/doctor/profile",
    "/profile/doctor",
  ];

  const PATIENT_PREFIXES = [
    "/home/patient",
    "/settings/patient",
    "/profile-settings/patient",
    "/patient/",
    "/patient/profile",
    "/profile/patient",
  ];

  const PRACTICE_TEAM_PREFIXES = [
    "/practice-team/welcome",
    "/practice-team/tasks",
    "/practice-team/appointments",
    "/practice-team/messages",
    "/practice-team/patients",
    "/practice-team/billing",
    "/practice-team/practice",
    "/practice-team/notifications",
  ];

  if (PRACTICE_TEAM_PREFIXES.some((p) => pathname.startsWith(p))) return "practice_team";
  if (DOCTOR_PREFIXES.some((p) => pathname.startsWith(p))) return "doctor";
  if (PATIENT_PREFIXES.some((p) => pathname.startsWith(p))) return "patient";
  return null;
}

/**
 * Optional route-based hiding (extra guard).
 * Keep it conservative; SSOT marker is the main control.
 */
function shouldHideTabsForRoute(pathname) {
  const HIDE_PREFIXES = [
    "/appointments/payment",
    "/appointments/edit",
    "/appointments/visit",
    "/appointments/prescriptions",
    "/prescriptions/",
    "/regional-health",
    "/settings/doctor/login-security/two-factor",
    "/settings/patient/login-security/two-factor",
    "/auth/",
    "/onboarding/",
  ];
  return HIDE_PREFIXES.some((p) => pathname.startsWith(p));
}

function shouldKeepTabsVisibleForBooking(pathname) {
  const SHOW_PREFIXES = [
    "/appointments/book",
    "/appointments/confirm",
    "/appointments/pay",
    "/appointments/checkout",
    "/appointments/success",
  ];
  return SHOW_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function MobileTabBar() {
  const location = useLocation();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const profile = auth.profile || null;
  const { unreadCount: unreadMessages } = useUnreadMessages(auth?.user?.id);
  const BYPASS =
  import.meta.env.DEV &&
  import.meta.env.VITE_BYPASS_VERIFICATION === "1";

  // ✅ hooks must be TOP LEVEL
  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const [pageHideTabs, setPageHideTabs] = React.useState(false);
  const [themeName, setThemeName] = React.useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-theme") || "light"
      : "light"
  );

  // Observe DOM markers (bottom sheets + page hide flag)
  React.useEffect(() => {
    const update = () => {
      setOverlayOpen(computeOverlayOpen());
      setPageHideTabs(computePageHideTabs());
    };

    update();

    const obs = new MutationObserver(update);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "aria-modal", "data-bottom-sheet", "data-hide-tabbar"],
    });

    return () => obs.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const root = document.documentElement;
    const updateTheme = () => {
      setThemeName(root.getAttribute("data-theme") || "light");
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // -------- ROLE (sticky, doctor route wins except patient-facing doctor profile) --------
  const rawPathRole = roleFromPathname(location.pathname);
  const profileRole = roleFromProfile(profile);
  const pathRole =
    location.pathname.startsWith("/doctor/profile") && profileRole === "patient"
      ? "patient"
      : rawPathRole;
  const lastRole = readLastRole(auth?.user?.id);

  // ✅ Prevent “patient by default” flash:
  // - if we don't know role yet, DO NOT render tabs
  const role = pathRole || profileRole || lastRole || null;

  React.useEffect(() => {
    const next = pathRole || profileRole || null;
    if (next === "doctor" || next === "patient") {
      writeLastRole(next, auth?.user?.id);
    }
  }, [pathRole, profileRole, auth?.user?.id]);

  const isMobile = window.matchMedia?.("(max-width: 767px)")?.matches ?? true;

  // ---------- AVATAR ----------
  const devDoctorPhoto = safeGetLocalStorage(DEV_DOCTOR_PHOTO_KEY);
  const devPatientPhoto = safeGetLocalStorage(DEV_PATIENT_PHOTO_KEY);
  const authProfilePhotoPath = String(
    profile?.profile_photo_path ||
      profile?.profile_photo_storage_path ||
      profile?.photo_path ||
      profile?.avatar_path ||
      ""
  ).trim();
  const authProfilePhotoUrl = String(
    profile?.profile_image_url || profile?.avatar_url || ""
  ).trim();
  const cachedAvatarSrc = React.useMemo(
    () => readAvatarSessionCache({ role, userId: auth?.user?.id }),
    [auth?.user?.id, role]
  );
  const [rolePhotoPath, setRolePhotoPath] = React.useState(
    () => authProfilePhotoPath
  );
  const [rolePhotoUrl, setRolePhotoUrl] = React.useState(
    () => authProfilePhotoUrl
  );

  const profilePhoto = rolePhotoUrl || authProfilePhotoUrl;
  const profilePhotoPath = rolePhotoPath || authProfilePhotoPath;

  React.useEffect(() => {
    let alive = true;

    async function hydrateRoleAvatar() {
      if (!auth?.user?.id || !role) {
        if (alive) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
        }
        return;
      }

      if (role === "practice_team") {
        if (alive) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
        }
        return;
      }

      const table = role === "doctor" ? "doctor_profiles" : "patient_profiles";
      try {
        const { data, error } = await supabase
          .from(table)
          .select("*")
          .eq("user_id", auth.user.id)
          .maybeSingle();

        if (!alive) return;
        if (error || !data) {
          if (!authProfilePhotoPath && !authProfilePhotoUrl) {
            setRolePhotoPath("");
            setRolePhotoUrl("");
            clearAvatarSessionCache({ role, userId: auth.user.id });
          }
          return;
        }

        const nextPath = String(
          data.profile_photo_path ||
            data.profile_photo_storage_path ||
            data.photo_path ||
            data.avatar_path ||
            ""
        ).trim();
        const nextUrl = String(
          data.profile_image_url ||
            data.profile_photo_url ||
            data.photo_url ||
            data.avatar_url ||
            ""
        ).trim();

        setRolePhotoPath(nextPath);
        setRolePhotoUrl(nextUrl);

        if (!nextPath && !nextUrl && !authProfilePhotoPath && !authProfilePhotoUrl) {
          clearAvatarSessionCache({ role, userId: auth.user.id });
        }
      } catch {
        if (!alive) return;
        if (!authProfilePhotoPath && !authProfilePhotoUrl) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
          clearAvatarSessionCache({ role, userId: auth.user.id });
        }
      }
    }

    hydrateRoleAvatar();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, authProfilePhotoPath, authProfilePhotoUrl, role]);

  // ✅ BYPASS must be role-specific (prevents patient photo “clinging” on doctor)
  const devPhoto = BYPASS
    ? role === "doctor"
      ? devDoctorPhoto
      : role === "patient"
      ? devPatientPhoto
      : ""
    : "";

  const [resolvedAvatarSrc, setResolvedAvatarSrc] = React.useState(
    () => cachedAvatarSrc || profilePhoto || devPhoto || ""
  );

  React.useEffect(() => {
    let alive = true;

    async function resolveAvatar() {
      let next = cachedAvatarSrc || profilePhoto || devPhoto || "";

      if (next && alive) {
        setResolvedAvatarSrc(next);
      }

      if (!role) {
        if (alive) setResolvedAvatarSrc(devPhoto || "");
        return;
      }

      if (role === "practice_team") {
        if (alive) setResolvedAvatarSrc("");
        return;
      }

      const bucket =
        role === "doctor"
          ? "doctor-profile-images"
          : "patient-profile-images";

      if (profilePhotoPath) {
        try {
          const storage = supabase.storage.from(bucket);
          const { data, error } = await signProfileAsset(supabase, bucket, profilePhotoPath);
          if (!error && data?.signedUrl) {
            next = data.signedUrl;
          } else {
            const { data: publicData } = storage.getPublicUrl(profilePhotoPath);
            next = publicData?.publicUrl || next;
          }
        } catch {
          // ignore and continue fallback chain
        }
      }

      if (!next && profilePhoto && /^https?:\/\//i.test(profilePhoto)) {
        next = profilePhoto;
      }

      if (!next) next = devPhoto || "";
      if (!alive) return;
      setResolvedAvatarSrc(next);
      if (next) {
        writeAvatarSessionCache({ role, userId: auth?.user?.id, src: next });
      }
    }

    resolveAvatar();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, cachedAvatarSrc, devPhoto, profilePhoto, profilePhotoPath, role]);

  // The signed URL cache is valid for the session and must be visible before
  // any fresh profile/storage request settles during navigation.
  const avatarSrc = cachedAvatarSrc || resolvedAvatarSrc || profilePhoto || devPhoto || "";
  const keepTabsVisibleForBooking = shouldKeepTabsVisibleForBooking(location.pathname);
  const isDarkTheme = themeName === "dark";
  const { t } = useLanguage();

// ✅ Page-level hide marker wins by default, except on the booking/payment flow
// routes that explicitly need the tab bar available.
if (pageHideTabs && !keepTabsVisibleForBooking) return null;

// ✅ Overlay (bottom sheets/dialogs): hide ONLY on mobile
if (isMobile && overlayOpen && !keepTabsVisibleForBooking) return null;

// ✅ Optional route-based hiding: keep ONLY on mobile (conservative)
if (isMobile && shouldHideTabsForRoute(location.pathname) && !keepTabsVisibleForBooking) return null;

  // ✅ If role unknown (loading), don't show patient tabs by accident
  if (!role) return null;

  const isPracticeTeam = role === "practice_team";
  const HOME_PATH = role === "doctor" ? "/home/doctor" : isPracticeTeam ? "/practice-team/welcome" : "/home/patient";
  const PROFILE_PATH = role === "doctor" ? "/doctor/profile" : isPracticeTeam ? "" : "/patient/profile";
  const APPOINTMENTS_PATH = role === "doctor" ? "/doctors/appointments" : isPracticeTeam ? "/practice-team/appointments" : "/appointments";
  const MESSAGES_PATH = resolveMessagesTabTarget(auth?.user?.id, location.pathname);

  const TABS = isPracticeTeam
    ? [
        {
          to: "/practice-team/welcome",
          label: t("navigation.tabs.home", "Home"),
          icon: "/icons/home_health_filled.svg",
          iconActive: "/icons/home_health_filled.svg",
          match: (path) => path === "/practice-team/welcome",
        },
        {
          to: "/practice-team/tasks",
          label: t("settings.practiceTeam.tasks.title", "My Tasks"),
          icon: "/icons/description.svg",
          iconActive: "/icons/description.svg",
          match: (path) => path === "/practice-team/tasks",
        },
        {
          to: "/practice-team/appointments",
          label: t("navigation.tabs.appointments", "Appointments"),
          icon: "/icons/appointments_inactive.svg",
          iconActive: "/icons/appointments_active.svg",
          match: (path) => path === "/practice-team/appointments",
        },
        {
          to: "/practice-team/messages",
          label: t("navigation.tabs.messages", "Messages"),
          icon: "/icons/messages_inactive.svg",
          iconActive: "/icons/messages_active.svg",
          match: (path) => path === "/practice-team/messages",
        },
        {
          to: "",
          label: t("navigation.tabs.profile", "Me"),
          icon: "",
          iconActive: "",
          isAvatar: true,
          disabled: true,
          match: () => false,
        },
      ]
    : [
    {
      to: HOME_PATH,
      label: t("navigation.tabs.home", "Home"),
      icon: "/icons/home_inactive.svg",
      iconActive: "/icons/home_active.svg",
      match: (path) => path === "/" || path === "/home" || path.startsWith("/home/"),
    },
    {
      to: APPOINTMENTS_PATH,
      label: t("navigation.tabs.appointments", "Appointments"),
      icon: "/icons/appointments_inactive.svg",
      iconActive: "/icons/appointments_active.svg",
      match: (path) =>
        role === "doctor"
          ? path === "/doctors/appointments" || path.startsWith("/doctors/appointments/")
          : path === "/appointments" || path.startsWith("/appointments/"),
    },
    {
      to: MESSAGES_PATH,
      label: t("navigation.tabs.messages", "Messages"),
      badgeCount: unreadMessages,
      icon: "/icons/messages_inactive.svg",
      iconActive: "/icons/messages_active.svg",
      match: (path) => path === "/messages" || path.startsWith("/messages/"),
    },
    {
      to: PROFILE_PATH,
      label: t("navigation.tabs.profile", "Profile"),
      icon: "",
      iconActive: "",
      isAvatar: true,
      match: (path) =>
        path.startsWith("/settings/") ||
        path.startsWith("/profile-settings/") ||
        path.startsWith("/patient/profile") ||
        path.startsWith("/doctor/profile") ||
        path.startsWith("/profile/"),
    },
  ];

  const navNode = (
    <nav
      className="
        md:hidden
        z-[50]
        fixed inset-x-0 bottom-0
        bg-[rgb(var(--clr-surface-cont-highest))]
        border-t border-[rgba(15,23,42,0.06)]
      "
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="mx-auto w-full max-w-[720px] px-4">
        <div className="h-[72px] flex items-center justify-between">
          {TABS.map((t) => {
            const isActive = !!t.match?.(location.pathname);

            const iconClassName =
              isActive && isDarkTheme ? "brightness-0 invert" : "";

            const iconOnly = t.isAvatar ? (
              avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="h-11 w-11 rounded-full object-cover"
                  draggable="false"
                />
              ) : (
                <img
                  src={PROFILE_AVATAR_PLACEHOLDER}
                  alt=""
                  className="h-11 w-11 rounded-full object-cover"
                  draggable="false"
                />
              )
            ) : isPracticeTeam ? (
              <span
                aria-hidden="true"
                className={[
                  "relative z-10 inline-block h-6 w-6 shrink-0",
                  isActive ? "bg-[#040812]" : "bg-[#5B6B86]",
                ].join(" ")}
                style={{
                  WebkitMask: `url(${isActive ? t.iconActive : t.icon}) center / contain no-repeat`,
                  mask: `url(${isActive ? t.iconActive : t.icon}) center / contain no-repeat`,
                }}
              />
            ) : (
              <IconImg
                src={isActive ? t.iconActive : t.icon}
                className={iconClassName}
              />
            );

            const iconNode = t.isAvatar ? (
              iconOnly
            ) : (
              <div
                className={`
                  relative
                  h-11 w-11 rounded-xl
                  flex items-center justify-center
                  ${
                    isActive
                      ? "border border-[rgba(var(--afrimed-primary),0.16)] bg-[var(--state-primary-12)] shadow-none"
                      : "bg-transparent"
                  }
                `}
              >
                {iconOnly}
                {Number(t?.badgeCount || 0) > 0 ? (
                  <NavAlertBadge
                    count={t.badgeCount}
                    className="absolute -right-1 top-[3px]"
                  />
                ) : null}
              </div>
            );

            const itemClassName = `
              flex flex-col items-center justify-center
              w-[72px]
              gap-1
              ${isActive ? "text-primary" : "text-on-surface-var"}
              ${t.disabled ? "cursor-not-allowed opacity-45" : ""}
            `;

            if (t.disabled) {
              return (
                <button
                  key={t.label}
                  type="button"
                  className={itemClassName}
                  aria-disabled="true"
                  tabIndex={-1}
                >
                  {iconNode}
                  <span className="text-label-small">{t.label}</span>
                </button>
              );
            }

            return (
              <NavLink
                key={t.label}
                to={t.to}
                className={() => itemClassName}
              >
                {iconNode}
                <span className="text-label-small">{t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );

  if (typeof document === "undefined") return navNode;
  return createPortal(navNode, document.body);
}
